import React, { useState } from 'react';
import { Binary, Copy, Check, Hash } from 'lucide-react';

type Base = 'binary' | 'octal' | 'decimal' | 'hexadecimal';

const baseConfig: Record<Base, { radix: number; prefix: string; label: string; placeholder: string }> = {
  binary: { radix: 2, prefix: '0b', label: 'Binary (Base 2)', placeholder: '1010' },
  octal: { radix: 8, prefix: '0o', label: 'Octal (Base 8)', placeholder: '12' },
  decimal: { radix: 10, prefix: '', label: 'Decimal (Base 10)', placeholder: '10' },
  hexadecimal: { radix: 16, prefix: '0x', label: 'Hexadecimal (Base 16)', placeholder: 'A' },
};

const NumberBaseConverter: React.FC = () => {
  const [values, setValues] = useState<Record<Base, string>>({
    binary: '',
    octal: '',
    decimal: '',
    hexadecimal: '',
  });
  const [activeBase, setActiveBase] = useState<Base>('decimal');
  const [copied, setCopied] = useState<Base | null>(null);
  const [error, setError] = useState('');

  const isValidForBase = (value: string, base: Base): boolean => {
    if (!value) return true;
    const radix = baseConfig[base].radix;
    const validChars = '0123456789abcdef'.slice(0, radix);
    return value.toLowerCase().split('').every(c => validChars.includes(c));
  };

  const handleChange = (base: Base, value: string) => {
    // Remove prefix if present
    let cleanValue = value.toLowerCase()
      .replace(/^0b/, '')
      .replace(/^0o/, '')
      .replace(/^0x/, '')
      .replace(/\s/g, '');

    if (!isValidForBase(cleanValue, base)) {
      setError(`Invalid character for ${baseConfig[base].label}`);
      return;
    }
    setError('');
    setActiveBase(base);

    if (!cleanValue) {
      setValues({ binary: '', octal: '', decimal: '', hexadecimal: '' });
      return;
    }

    try {
      const decimalValue = parseInt(cleanValue, baseConfig[base].radix);
      if (isNaN(decimalValue)) {
        setError('Invalid number');
        return;
      }

      setValues({
        binary: decimalValue.toString(2),
        octal: decimalValue.toString(8),
        decimal: decimalValue.toString(10),
        hexadecimal: decimalValue.toString(16).toUpperCase(),
      });
    } catch {
      setError('Conversion error');
    }
  };

  const copyToClipboard = async (base: Base) => {
    const value = values[base];
    if (!value) return;
    await navigator.clipboard.writeText(baseConfig[base].prefix + value);
    setCopied(base);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatBinary = (bin: string): string => {
    return bin.replace(/(.{4})/g, '$1 ').trim();
  };

  const commonValues = [
    { dec: 255, label: '255 (Max byte)' },
    { dec: 256, label: '256' },
    { dec: 1024, label: '1024 (1KB)' },
    { dec: 65535, label: '65535 (Max 16-bit)' },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Number Base Converter</h2>
        <p className="text-gray-600">Convert between binary, octal, decimal, and hexadecimal</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Converter Inputs */}
        <div className="space-y-4">
          {(Object.keys(baseConfig) as Base[]).map((base) => (
            <div
              key={base}
              className={`bg-white rounded-xl border-2 p-4 transition-colors ${
                activeBase === base ? 'border-amber-500' : 'border-gray-200'
              }`}
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {baseConfig[base].label}
              </label>
              <div className="flex gap-2">
                {baseConfig[base].prefix && (
                  <span className="px-3 py-2 bg-gray-100 rounded-lg font-mono text-gray-600">
                    {baseConfig[base].prefix}
                  </span>
                )}
                <input
                  type="text"
                  value={base === 'binary' ? formatBinary(values[base]) : values[base]}
                  onChange={(e) => handleChange(base, e.target.value.replace(/\s/g, ''))}
                  className="flex-1 px-4 py-2 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder={baseConfig[base].placeholder}
                />
                <button
                  onClick={() => copyToClipboard(base)}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {copied === base ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Quick Values */}
        <div className="bg-gray-50 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">Common Values</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {commonValues.map((item) => (
              <button
                key={item.dec}
                onClick={() => handleChange('decimal', item.dec.toString())}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-100 transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bit Operations Preview */}
        {values.decimal && (
          <div className="bg-gray-900 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Binary className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-gray-400">Bit Representation</span>
            </div>
            <div className="font-mono text-lg">
              <div className="flex flex-wrap gap-1">
                {values.binary.padStart(Math.ceil(values.binary.length / 8) * 8, '0').split('').map((bit, i) => (
                  <span
                    key={i}
                    className={`w-6 h-8 flex items-center justify-center rounded ${
                      bit === '1' ? 'bg-amber-500 text-white' : 'bg-gray-700 text-gray-400'
                    } ${i > 0 && i % 4 === 0 ? 'ml-2' : ''}`}
                  >
                    {bit}
                  </span>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {values.binary.length} bits • {Math.ceil(values.binary.length / 8)} bytes
              </div>
            </div>
          </div>
        )}

        {/* Reference */}
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
          <div className="flex items-start gap-3">
            <Hash className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900 mb-2">Quick Reference</h4>
              <div className="text-sm text-amber-700 grid grid-cols-2 gap-x-4">
                <p>Binary: 0-1 (0b prefix)</p>
                <p>Octal: 0-7 (0o prefix)</p>
                <p>Decimal: 0-9 (no prefix)</p>
                <p>Hex: 0-9, A-F (0x prefix)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NumberBaseConverter;
